"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Loader2, Search } from "lucide-react";

import { Input } from "@/components/ui/Input";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useProductSearch } from "@/features/shop/customer/customer-shop.queries";
import { getProductImages } from "@/features/shop/types/customer-shop";
import { cn } from "@/lib/utils";

interface HeaderProductSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}

/** Header search box: debounces input, queries products by name, and shows a
 * clickable results dropdown that navigates to the product's detail page. */
export function HeaderProductSearch({
  value,
  onChange,
  placeholder = "Search products...",
  className,
  inputClassName,
}: HeaderProductSearchProps) {
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const debouncedValue = useDebouncedValue(value, 350);
  const trimmed = debouncedValue.trim();
  const { data, isFetching } = useProductSearch(trimmed);
  const results = data?.data ?? [];
  const showDropdown = open && trimmed.length >= 2;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSelect(productId: string) {
    setOpen(false);
    router.push(`/shop/products/${productId}`);
  }

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className={inputClassName}
      />

      {showDropdown ? (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-lg">
          {isFetching ? (
            <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching...
            </div>
          ) : results.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">
              No products found for &ldquo;{trimmed}&rdquo;
            </div>
          ) : (
            <ul className="max-h-80 overflow-y-auto py-1">
              {results.map((product) => {
                const imageUrl = getProductImages(product)[0];
                const shopName =
                  typeof product.shopId === "object" && product.shopId
                    ? product.shopId.name
                    : undefined;

                return (
                  <li key={product._id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(product._id)}
                      className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-accent"
                    >
                      <span className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-muted">
                        {imageUrl ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            src={imageUrl}
                            alt={product.name}
                            className="h-full w-full object-cover"
                          />
                        ) : null}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground">
                          {product.name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {shopName ? `${shopName} · ` : ""}KES {product.price}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
